/**
 * Service worker registration for the Business OS shell.
 *
 * Everything that touches a browser global is reachable through an injection
 * seam (`navigatorRef` / `cachesRef`) so the behaviour can be exercised in
 * tests without a real service worker.
 */

export type RegisterOptions = {
  enabled: boolean;
  kill?: boolean;
  navigatorRef?: any;
  cachesRef?: any;
  onUpdate?: (reg: any) => void;
};

export type RegisterResult =
  | "unsupported"
  | "killed"
  | "kill_failed"
  | "disabled"
  | "registered"
  | "failed";

/** Guards the one-shot `controllerchange` handler against reload loops. */
let reloadOnControllerChangeBound = false;

/** Only reload on `controllerchange` when the user explicitly asked for an update. */
let updateRequested = false;

function hasServiceWorker(nav: any): boolean {
  return !!nav && "serviceWorker" in nav;
}

function defaultCaches(): any {
  try {
    return typeof caches !== "undefined" ? (caches as any) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Purges every `mb-*` cache. A missing caches API is not a failure; a rejected
 * `keys()` or `delete()` is reported back to the caller.
 */
async function purgeMbCaches(cachesRef: any): Promise<boolean> {
  if (!cachesRef) return true;
  let keys: any[] = [];
  try {
    keys = (await cachesRef.keys()).slice();
  } catch {
    return false;
  }
  let ok = true;
  await Promise.all(
    keys.map(async (name: any) => {
      if (typeof name === "string" && name.startsWith("mb-")) {
        try {
          await cachesRef.delete(name);
        } catch {
          ok = false;
        }
      }
    }),
  );
  return ok;
}

/**
 * Adds the PWA install metadata to `document.head` on demand, only when the PWA
 * is actually enabled and only if the tags are not already present.
 */
function ensureInstallMetadata(): void {
  if (typeof document === "undefined") return;
  const head = document.head;
  if (!head || typeof head.appendChild !== "function") return;

  const specs: Array<{
    tag: "link" | "meta";
    selector: string;
    attrs: Record<string, string>;
  }> = [
    {
      tag: "link",
      selector: 'link[rel="manifest"]',
      attrs: { rel: "manifest", href: "/manifest.webmanifest" },
    },
    {
      tag: "link",
      selector: 'link[rel="apple-touch-icon"]',
      attrs: { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png" },
    },
    {
      tag: "meta",
      selector: 'meta[name="mobile-web-app-capable"]',
      attrs: { name: "mobile-web-app-capable", content: "yes" },
    },
  ];

  for (const spec of specs) {
    try {
      if (head.querySelector && head.querySelector(spec.selector)) continue;
      const el = document.createElement(spec.tag);
      for (const key of Object.keys(spec.attrs)) {
        el.setAttribute(key, spec.attrs[key]);
      }
      head.appendChild(el);
    } catch {
      /* ignore individual metadata failures */
    }
  }
}

function bindControllerChangeReload(serviceWorker: any): void {
  if (reloadOnControllerChangeBound) return;
  if (!serviceWorker || typeof serviceWorker.addEventListener !== "function") return;
  reloadOnControllerChangeBound = true;
  serviceWorker.addEventListener("controllerchange", () => {
    if (!updateRequested) return;
    updateRequested = false;
    try {
      if (typeof window !== "undefined" && window.location) {
        window.location.reload();
      }
    } catch {
      /* ignore reload failures */
    }
  });
}

export async function registerServiceWorker(o: RegisterOptions): Promise<RegisterResult> {
  const nav: any =
    o.navigatorRef !== undefined
      ? o.navigatorRef
      : typeof navigator !== "undefined"
        ? (navigator as any)
        : undefined;

  if (!hasServiceWorker(nav)) return "unsupported";

  const serviceWorker = nav.serviceWorker;

  if (o.kill) {
    const cachesRef = o.cachesRef !== undefined ? o.cachesRef : defaultCaches();
    let ok = true;
    let registrations: any[] = [];
    try {
      registrations = (await serviceWorker.getRegistrations()).slice();
    } catch {
      ok = false;
      registrations = [];
    }
    await Promise.all(
      registrations.map(async (reg: any) => {
        try {
          await reg.unregister();
        } catch {
          ok = false;
        }
      }),
    );
    const purged = await purgeMbCaches(cachesRef);
    if (!purged) ok = false;
    return ok ? "killed" : "kill_failed";
  }

  if (!o.enabled) return "disabled";

  try {
    ensureInstallMetadata();
    const reg = await serviceWorker.register("/sw.js", { scope: "/" });
    bindControllerChangeReload(serviceWorker);

    /* Surface an update that is already waiting (e.g. registered on reload). */
    if (reg && reg.waiting && nav.serviceWorker.controller && typeof o.onUpdate === "function") {
      o.onUpdate(reg);
    }

    if (reg && typeof reg.addEventListener === "function") {
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing || typeof installing.addEventListener !== "function") return;
        installing.addEventListener("statechange", () => {
          const isUpdate =
            installing.state === "installed" && !!nav.serviceWorker.controller;
          if (isUpdate && typeof o.onUpdate === "function") {
            o.onUpdate(reg);
          }
        });
      });
    }
    return "registered";
  } catch {
    return "failed";
  }
}

export function applyUpdate(reg: any): void {
  if (!reg) return;
  const waiting = reg.waiting;
  if (!waiting) return;
  try {
    if (typeof waiting.postMessage === "function") {
      updateRequested = true;
      waiting.postMessage({ type: "SKIP_WAITING" });
    }
  } catch {
    /* ignore postMessage failures */
  }
}
